/**
 * Embedded Chart Component
 * Renders generic chart data within chat messages.
 */

import { Dimensions, StyleSheet, Text, View } from '@/components/common';
import {
  VictoryArea,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryPie,
  VictoryTheme,
} from 'victory';
import { useTranslation } from '@/lib/i18n';
import { embeddedChartDesign as design } from './embeddedDesign';

type ChartType = 'line' | 'bar' | 'pie' | 'area' | 'scatter';

export interface EmbeddedChartProps {
  data: {
    id?: string;
    title?: string;
    type?: ChartType;
    chartType?: ChartType;
    data?: Array<Record<string, any>>;
    labels?: string[];
    datasets?: Array<{
      label?: string;
      data?: Array<number | string>;
      backgroundColor?: string | string[];
      borderColor?: string | string[];
    }>;
    xAxisKey?: string;
    yAxisKey?: string;
    xAxis?: string;
    yAxis?: string;
    dataKeys?: string[];
    colors?: string[];
    summary?: Record<string, any>;
  };
}

function firstKeyMatching(row: Record<string, any>, predicate: (value: any) => boolean): string | undefined {
  return Object.keys(row).find((key) => predicate(row[key]));
}

function firstKeyMatchingEntry(row: Record<string, any>, predicate: (key: string, value: any) => boolean): string | undefined {
  return Object.keys(row).find((key) => predicate(key, row[key]));
}

function isNumericLike(value: any): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length > 0 && Number.isFinite(Number(trimmed));
}

function resolveKeys(rows: Array<Record<string, any>>, data: EmbeddedChartProps['data']) {
  const first = rows[0] || {};
  const xAxisKey =
    data.xAxisKey ||
    data.xAxis ||
    firstKeyMatchingEntry(first, (key) => /date|month|week|period|time|label|name|title/i.test(key)) ||
    firstKeyMatching(first, (value) => typeof value === 'string' && !isNumericLike(value)) ||
    firstKeyMatching(first, (value) => value instanceof Date) ||
    'label';
  const yAxisKey =
    data.yAxisKey ||
    data.yAxis ||
    data.dataKeys?.[0] ||
    firstKeyMatchingEntry(
      first,
      (key, value) => /avg|average|value|count|student|session|rate|total|number|metric/i.test(key) && isNumericLike(value)
    ) ||
    firstKeyMatching(first, isNumericLike) ||
    'value';

  return { xAxisKey, yAxisKey };
}

function toChartRows(rows: Array<Record<string, any>>, xAxisKey: string, yAxisKey: string) {
  return rows
    .map((row, index) => {
      const yRaw = row[yAxisKey];
      const y = typeof yRaw === 'number' ? yRaw : Number(yRaw);
      if (!Number.isFinite(y)) return null;

      const rawLabel = row[xAxisKey] ?? row.label ?? row.name ?? row.title ?? `Item ${index + 1}`;
      return {
        x: index + 1,
        y,
        label: String(rawLabel),
      };
    })
    .filter((row): row is { x: number; y: number; label: string } => Boolean(row));
}

function rowsFromChartJsShape(data: EmbeddedChartProps['data']): Array<Record<string, any>> {
  const labels = Array.isArray(data?.labels) ? data.labels : [];
  const firstDataset = Array.isArray(data?.datasets) ? data.datasets[0] : undefined;
  const values = Array.isArray(firstDataset?.data) ? firstDataset.data : [];
  if (!labels.length || !values.length) return [];

  const valueKey = firstDataset?.label || 'value';
  return labels.map((label, index) => ({
    label,
    [valueKey]: values[index],
  }));
}

function compactNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function EmbeddedChart({ data }: EmbeddedChartProps) {
  const { t } = useTranslation();
  const rows = Array.isArray(data?.data) && data.data.length > 0
    ? data.data
    : rowsFromChartJsShape(data || {});
  const chartType = data?.type || data?.chartType || 'bar';
  const chartWidth = Math.max(
    design.chartMinWidth,
    Math.min(Dimensions.get('window').width - design.chartViewportGutter, design.chartMaxWidth)
  );
  const { xAxisKey, yAxisKey } = resolveKeys(rows, data || {});
  const chartRows = toChartRows(rows, xAxisKey, yAxisKey);

  if (!chartRows.length) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>{data?.title || t('embedded.chart.defaultTitle', 'Chart')}</Text>
        <Text style={styles.emptyText}>{t('embedded.chart.noData', 'No chartable data was returned.')}</Text>
      </View>
    );
  }

  const values = chartRows.map((row) => row.y);
  const total = values.reduce((sum, value) => sum + value, 0);
  const average = total / values.length;
  const seriesRows = chartRows.map(({ x, y }) => ({ x, y }));
  const chartJsBackground = Array.isArray(data?.datasets?.[0]?.backgroundColor)
    ? data?.datasets?.[0]?.backgroundColor?.[0]
    : data?.datasets?.[0]?.backgroundColor;
  const chartJsBorder = Array.isArray(data?.datasets?.[0]?.borderColor)
    ? data?.datasets?.[0]?.borderColor?.[0]
    : data?.datasets?.[0]?.borderColor;
  const color = data?.colors?.[0] || chartJsBorder || chartJsBackground || design.chartPrimaryColor;
  const isAverageMetric = /avg|average|rate|percent|percentage/i.test(yAxisKey);

  const chart =
    chartType === 'pie' ? (
      <VictoryPie
        width={chartWidth}
        height={design.pieHeight}
        data={chartRows}
        x="label"
        y="y"
        colorScale={data?.colors || [...design.chartColorScale]}
        labels={({ datum }) => `${datum.label}: ${compactNumber(datum.y)}`}
        innerRadius={design.pieInnerRadius}
        padding={design.piePadding}
      />
    ) : (
      <VictoryChart
        width={chartWidth}
        height={design.chartHeight}
        theme={VictoryTheme.material}
        padding={design.chartPadding}
      >
        <VictoryAxis
          tickValues={chartRows.map((row) => row.x)}
          tickFormat={chartRows.map((row) => row.label)}
          style={{
            tickLabels: {
              fontSize: design.xAxisFontSize,
              padding: design.axisPadding,
              angle: chartRows.length > design.xTickAngleThreshold ? design.xTickAngle : 0,
            },
            grid: { stroke: 'transparent' },
          }}
        />
        <VictoryAxis
          dependentAxis
          tickFormat={(value: number) => compactNumber(value)}
          style={{
            tickLabels: { fontSize: design.axisFontSize, padding: design.axisPadding },
            grid: { stroke: design.gridStroke, strokeDasharray: '4,4' },
          }}
        />
        {chartType === 'line' || chartType === 'scatter' ? (
          <VictoryLine data={seriesRows} style={{ data: { stroke: color, strokeWidth: design.lineStrokeWidth } }} />
        ) : chartType === 'area' ? (
          <VictoryArea
            data={seriesRows}
            style={{ data: { fill: color, fillOpacity: 0.22, stroke: color, strokeWidth: design.areaStrokeWidth } }}
          />
        ) : (
          <VictoryBar data={seriesRows} style={{ data: { fill: color } }} />
        )}
      </VictoryChart>
    );

  return (
    <View style={styles.container}>
      {data?.title ? <Text style={styles.title}>{data.title}</Text> : null}
      {chart}

      <View style={styles.summary}>
        {!isAverageMetric ? (
          <Text style={styles.summaryText}>
            {t('embedded.chart.total', 'Total')}: {compactNumber(total)}
          </Text>
        ) : null}
        <Text style={styles.summaryText}>
          {t('embedded.chart.average', 'Average')}: {compactNumber(average)}
        </Text>
        <Text style={styles.summaryText}>
          {t('embedded.chart.points', 'Points')}: {chartRows.length}
        </Text>
      </View>
    </View>
  );
}

export default EmbeddedChart;

const styles = StyleSheet.create({
  container: {
    marginTop: design.containerMarginTop,
    marginHorizontal: design.containerMarginHorizontal,
    backgroundColor: design.containerBackgroundColor,
    borderRadius: design.containerRadius,
    padding: design.containerPadding,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: design.borderColor,
  },
  title: {
    width: '100%',
    fontSize: design.titleFontSize,
    lineHeight: design.titleLineHeight,
    fontWeight: '600',
    marginBottom: design.titleMarginBottom,
    color: design.titleColor,
  },
  summary: {
    width: '100%',
    marginTop: design.summaryMarginTop,
    padding: design.summaryPadding,
    backgroundColor: design.summaryBackgroundColor,
    borderRadius: design.summaryRadius,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: design.summaryGap,
  },
  summaryText: {
    fontSize: design.summaryTextFontSize,
    lineHeight: design.summaryTextLineHeight,
    color: design.summaryTextColor,
  },
  emptyText: {
    width: '100%',
    fontSize: design.emptyFontSize,
    lineHeight: design.emptyLineHeight,
    color: design.emptyTextColor,
  },
});
